<script setup lang="ts">
const { t } = useI18n({
  useScope: 'local',
});

const props = defineProps({
  progress: {
    type: Number,
    required: true,
    default: 0,
  },
  color: {
    type: String,
    required: false,
    default: "#fa9400",
  },
  emptyColor: {
    type: String,
    required: false,
    default: "#fee3ba",
  },
  backgroundColor: {
    type: String,
    required: false,
    default: "#fee3ba",
  },
  strokeWidth: {
    type: Number,
    required: false,
    default: 3,
  },
  ringSize: {
    type: Number,
    required: false,
    default: 60,
  },
  fontSize: {
    type: String,
    required: false,
    default: null,
  },
});

const computedFontSize = computed(() => props.fontSize || `${props.ringSize * 0.15}px`);
</script>

<template>
  <div class="donut-progress" :style="{ '--size': ringSize + 'px' }">
    <svg :width="ringSize" :height="ringSize" viewBox="0 0 36 36" class="donut" role="img" :aria-label="`${t('progress')}: ${progress}%`">
      <circle
        class="donut-ring"
        cx="18"
        cy="18"
        r="15.915"
        fill="transparent"
        :stroke="progress === 0 ? emptyColor : backgroundColor"
        :stroke-width="strokeWidth"
      />
      <circle
        v-if="progress > 0"
        class="donut-segment"
        :class="{ green: progress === 100 }"
        cx="18"
        cy="18"
        r="15.915"
        fill="transparent"
        :stroke="color"
        :stroke-width="strokeWidth"
        :stroke-dasharray="`${progress}, 100`"
        stroke-linecap="round"
        style="transition: stroke-dasharray 0.3s ease"
      />
    </svg>
    <div class="donut-text" :style="{ color: color, fontSize: computedFontSize + 'px' }" :class="{ green: progress === 100 }">
      <span class="percentage">{{ progress }}%</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~/assets/styles/tw-variables.scss";

.donut-progress {
  position: relative;
  display: inline-block;
  width: var(--size, 120px);
  height: var(--size, 120px);
}

.donut {
  transform: rotate(-90deg);
  width: var(--size);
  height: var(--size);
}

.donut-ring,
.donut-segment {
  transition: stroke-dasharray 0.3s ease, stroke 0.3s ease;
}

.donut-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: calc(var(--size, 120px) * 0.15);
  font-weight: bold;
}

.percentage {
  font-size: inherit;
}
.green {
  color: $success !important;
  stroke: $success !important;
}

</style>

<i18n lang="json">
{
  "de": {
    "progress": "Fortschritt"
  },
  "en": {
    "progress": "Progress"
  }
}
</i18n>
